import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import type {
  RelacionLaboral,
  RelacionLaboralExpandida,
  RelacionLaboralCreatePayload,
  RelacionLaboralUpdatePayload,
} from '../types/relacionLaboral';

const QK = {
  byAfiliado: (afiliadoId: string) => ['relaciones_laborales', 'afiliado', afiliadoId] as const,
  byEmpresa: (empresaId: string) => ['relaciones_laborales', 'empresa', empresaId] as const,
};

// ----- LIST por afiliado -----

async function fetchByAfiliado(afiliadoId: string): Promise<RelacionLaboralExpandida[]> {
  const { data, error } = await supabase
    .from('relaciones_laborales')
    .select(
      `*,
       empresa:empresas(cuit, razon_social),
       sucursal:sucursales(denominacion, domicilio),
       categoria:categorias(codigo, descripcion)`
    )
    .eq('afiliado_id', afiliadoId)
    .order('fecha_alta', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as RelacionLaboralExpandida[];
}

export function useRelacionesLaboralesPorAfiliado(afiliadoId: string | undefined) {
  return useQuery({
    queryKey: afiliadoId ? QK.byAfiliado(afiliadoId) : ['relaciones_laborales', 'none'],
    queryFn: () => fetchByAfiliado(afiliadoId!),
    enabled: !!afiliadoId,
  });
}

// ----- LIST por empresa (usado en DDJJ Fase 4.3+) -----

async function fetchByEmpresa(empresaId: string): Promise<RelacionLaboralExpandida[]> {
  const { data, error } = await supabase
    .from('relaciones_laborales')
    .select(
      `*,
       afiliado:afiliados(cuil, apellido, nombre, estado),
       sucursal:sucursales(denominacion),
       categoria:categorias(codigo, descripcion)`
    )
    .eq('empresa_id', empresaId)
    .eq('estado', 'activa')
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as unknown as RelacionLaboralExpandida[];
}

export function useRelacionesLaboralesPorEmpresa(empresaId: string | undefined) {
  return useQuery({
    queryKey: empresaId ? QK.byEmpresa(empresaId) : ['relaciones_laborales', 'none'],
    queryFn: () => fetchByEmpresa(empresaId!),
    enabled: !!empresaId,
  });
}

// ----- CREATE -----

export function useCreateRelacionLaboral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: RelacionLaboralCreatePayload) => {
      const { data, error } = await supabase
        .from('relaciones_laborales')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as RelacionLaboral;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(data.afiliado_id) });
      qc.invalidateQueries({ queryKey: QK.byEmpresa(data.empresa_id) });
    },
  });
}

// ----- UPDATE -----

export function useUpdateRelacionLaboral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: RelacionLaboralUpdatePayload;
    }) => {
      const { data, error } = await supabase
        .from('relaciones_laborales')
        .update(payload)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as RelacionLaboral;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(data.afiliado_id) });
      qc.invalidateQueries({ queryKey: QK.byEmpresa(data.empresa_id) });
    },
  });
}

// ----- DAR DE BAJA -----

export function useDarDeBajaRelacionLaboral() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      fecha_baja,
      observaciones,
    }: {
      id: string;
      fecha_baja: string;
      observaciones?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('relaciones_laborales')
        .update({ estado: 'baja', fecha_baja, observaciones: observaciones ?? null })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as RelacionLaboral;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(data.afiliado_id) });
      qc.invalidateQueries({ queryKey: QK.byEmpresa(data.empresa_id) });
    },
  });
}
